import React, { ChangeEvent, ComponentProps } from 'react';

import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import Input from 'components/lib/Input';
import {
  SimpleListModal,
  SimpleListModalContent,
  SimpleListModalDescription,
  SimpleListModalHeaderActions,
  SimpleListModalTitleStrong,
} from 'components/lib/SimpleListModal';
import { SimpleListModalStickyFooter } from 'components/lib/SimpleListModalComponents';

import AvailableShopperContactsTable from './AvailableShopperContactsTable';
import ManageShopRequestShopperEmailsTable from './ManageShopRequestShopperEmailsTable';

const ContactLinksModal = styled(SimpleListModal)`
  .ant-modal-content {
    width: 920px;
  }

  .ant-modal-body {
    height: min(900px, calc(95vh - 80px));
  }
`;

const ModalControls = styled(SimpleListModalHeaderActions)`
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const SearchInput = styled(Input)`
  width: 420px;
`;

const ContactLists = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 20px;
  padding-right: 16px;
  overflow-y: auto;
`;

const ContactListSection = styled.section`
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 8px;

  &:first-child {
    flex: 0 1 35%;
  }

  &:last-child {
    flex: 0 1 55%;
  }
`;

const ContactListTitle = styled.h3`
  margin: 0;
  flex-shrink: 0;
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
`;

type Props = {
  open: boolean;
  singleUse: boolean;
  search: string;
  linkedContactsTableProps: ComponentProps<typeof ManageShopRequestShopperEmailsTable>;
  availableContactsTableProps: ComponentProps<typeof AvailableShopperContactsTable>;
  onAddContact: () => void;
  onClose: () => void;
  onSearchChange: (search: string) => void;
};

const ManageShopRequestShopperEmailsModalContent = ({
  open,
  singleUse,
  search,
  linkedContactsTableProps,
  availableContactsTableProps,
  onAddContact,
  onClose,
  onSearchChange,
}: Props) => {
  return (
    <ContactLinksModal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Manage Shopper Contact Links"
      centered
      width="fit-content"
      destroyOnClose
    >
      <SimpleListModalContent>
        <SimpleListModalDescription>
          <SimpleListModalTitleStrong>
            {singleUse
              ? 'Speed to Lead shopper contacts are single-use.'
              : 'Link reusable shopper contacts to this shop request.'}
          </SimpleListModalTitleStrong>
          {singleUse
            ? 'Linking permanently reserves the contact for this request, so it cannot be linked to another shop request.'
            : 'Use the shopper contacts directory for mailbox authentication and global email management.'}
        </SimpleListModalDescription>

        <ModalControls>
          <SearchInput
            value={search}
            prefix={<SearchOutlined />}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
            placeholder="Search shopper contacts"
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddContact}>
            Add Contact
          </Button>
        </ModalControls>

        <ContactLists>
          <ContactListSection>
            <ContactListTitle>Linked Shopper Contacts</ContactListTitle>
            <ManageShopRequestShopperEmailsTable {...linkedContactsTableProps} />
          </ContactListSection>

          <ContactListSection>
            <ContactListTitle>Available Shopper Contacts</ContactListTitle>
            <AvailableShopperContactsTable {...availableContactsTableProps} />
          </ContactListSection>
        </ContactLists>

        <SimpleListModalStickyFooter>
          <Button onClick={onClose}>Close</Button>
        </SimpleListModalStickyFooter>
      </SimpleListModalContent>
    </ContactLinksModal>
  );
};

export default ManageShopRequestShopperEmailsModalContent;
